import React, { useState, useEffect, startTransition } from 'react';
import { 
  Paper, 
  BottomNavigation, 
  BottomNavigationAction,
  Badge,
  useTheme
} from '@mui/material';
import { IoHomeOutline, IoHome } from "react-icons/io5";
import { FaRegHospital, FaHospital } from "react-icons/fa6";
import { BsCreditCard2Front, BsCreditCard2FrontFill } from "react-icons/bs";
import { GoBell, GoBellFill } from "react-icons/go";
import { FiSettings } from "react-icons/fi";
import { IoSettings } from "react-icons/io5";
import { Link, useLocation } from 'react-router-dom';
import { notificationApi } from '../../services/api';
import { BDANotificationsSocket } from '../../sockets/websocket_notifications';
import { useAuth } from '../../contexts/AuthContext';

const BottomNavBar = () => {
  const location = useLocation();
  const [value, setValue] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const theme = useTheme();
  const { token, isAuthenticated } = useAuth();
  
  useEffect(() => {
    if (!isAuthenticated || !token) {
      console.warn('BottomNavBar: Not authenticated or no token');
      return;
    }

    console.log('BottomNavBar: Initializing with token available:', !!token);
    const socket = new BDANotificationsSocket(token);
    let mounted = true;

    const handleUnreadCount = (count) => {
      console.log('BottomNavBar: Handling unread count:', count);
      if (mounted) {
        const numCount = Number(count);
        if (!isNaN(numCount)) {
          console.log('BottomNavBar: Setting unread count to:', numCount);
          setUnreadCount(numCount);
        }
      }
    };

    socket.on('unread_count', handleUnreadCount);
    
    socket.on('unread_notifications', (data) => {
      console.log('BottomNavBar: Received unread notifications:', data);
      if (mounted && data.unread_count !== undefined) {
        handleUnreadCount(data.unread_count);
      }
    });

    socket.on('notification', (data) => {
      console.log('BottomNavBar: Received new notification:', data);
      if (!mounted) return;
      
      if (data.unread_count !== undefined) {
        handleUnreadCount(data.unread_count);
      } else {
        setUnreadCount(prev => prev + 1);
      }
    });

    socket.on('connected', () => {
      console.log('BottomNavBar: Connected to WebSocket');
      if (mounted && socket.ws?.readyState === WebSocket.OPEN) {
        console.log('BottomNavBar: Requesting initial unread count');
        socket.ws.send(JSON.stringify({ 
          type: 'get_unread',
          timestamp: new Date().toISOString()
        }));
      }
    });

    socket.on('error', (error) => {
      console.error('BottomNavBar: WebSocket error:', error);
    });

    // Initial fetch
    const fetchInitialCount = async () => {
      try {
        const count = await notificationApi.getUnreadCount();
        if (mounted) {
          const numCount = Number(count);
          if (!isNaN(numCount)) {
            console.log('BottomNavBar: Initial count from API:', numCount);
            setUnreadCount(numCount);
          }
        }
      } catch (error) {
        console.error('BottomNavBar: Error fetching initial count:', error);
      }
    };
    fetchInitialCount();

    console.log('BottomNavBar: Connecting WebSocket');
    socket.connect();

    return () => {
      console.log('BottomNavBar: Cleaning up');
      mounted = false;
      socket.disconnect();
    };
  }, [token, isAuthenticated]); // Add dependencies
  
  // Set the active tab based on current path
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/bda/service-providers')) {
      setValue(1);
    } else if (path.includes('/bda/easyop-patients')) {
      setValue(2);
    } else if (path.includes('/bda/notifications')) {
      setValue(3);
    } else if (path.includes('/bda/settings')) {
      setValue(4);
    } else {
      // Default to dashboard
      setValue(0);
    }
  }, [location]);

  return (
    <Paper 
      sx={{ 
        position: 'fixed', 
        bottom: 0, 
        left: 0, 
        right: 0, 
        zIndex: 1000 
      }} 
      elevation={3}
    >
      <BottomNavigation
        value={value}
        onChange={(event, newValue) => {
          startTransition(() => {
            setValue(newValue);
          });
        }}
        showLabels
        sx={{ 
          background: theme.palette.primary.light,
          height: '70px',
          color: theme.palette.primary.dark,
          "& .MuiBottomNavigationAction-root": {
            "& svg": {
              fontSize: "1.5rem",
              color: theme.palette.primary.main,
              transition: 'color 0.3s ease'
            },
            "& .MuiBottomNavigationAction-label": {
              color: theme.palette.primary.main
            }
          },
          "& .Mui-selected": {
            "& svg": {
              color: theme.palette.primary.dark
            },
            "& .MuiBottomNavigationAction-label": {
              color: theme.palette.primary.dark
            }
          }
        }}
      >
        <BottomNavigationAction 
          component={Link}
          to="/bda/dashboard"
          label="Home" 
          icon={value === 0 ? <IoHome size={24}  /> : <IoHomeOutline size={24}  />}
        />
        <BottomNavigationAction 
          component={Link}
          to="/bda/service-providers"
          label="Providers" 
          icon={value === 1 ? <FaHospital size={24}  /> : <FaRegHospital size={24}  />}
        />
        <BottomNavigationAction 
          component={Link}
          to="/bda/easyop-patients"
          label="EasyOP" 
          icon={value === 2 ? <BsCreditCard2FrontFill size={24}  /> : <BsCreditCard2Front size={24} />}
        />
        <BottomNavigationAction 
          component={Link}
          to="/bda/notifications"
          label="Notifications" 
          icon={
            <Badge 
              badgeContent={unreadCount} 
              color="error" 
              max={99}
              sx={{
                '& .MuiBadge-badge': {
                  backgroundColor: '#ff4444',
                  color: 'white',
                }
              }}
            >
              {value === 3 ? <GoBellFill size={24}  /> : <GoBell size={24}  />}
            </Badge>
          } 
        />
        <BottomNavigationAction 
          component={Link}
          to="/bda/settings"
          label="Settings" 
          icon={value === 4 ? <IoSettings size={24} /> : <FiSettings size={24} />}
        />
      </BottomNavigation>
    </Paper>
  );
};

export default BottomNavBar; 